import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { RootState } from '../../store/store';
import {
  setChatMode,
  setRagChatType,
  setPromptFromLibrary,
  clearaskChatHistory,
  clearRagHistory,
  setSelectedTool,
  setPendingPromptSelection,
  clearPendingPromptSelection,
  setShowPromptConfirmation,
  cancelTranscribingMessages,
  type RagChatType,
} from '../../store/chatModeSlice';
import { clearUploadedFiles, clearAnalysisResults, clearTranslationData, resetSessionId } from '../../store/chatSlice';
import {
  setVoiceModeActive,
  setVoiceModeListening,
  setVoiceModePaused,
  setVoiceModeRestarting,
  setShowInactivityWarning,
  setInactivityCountdown,
  setDisconnectedByInactivity,
} from '../../store/voiceModeSlice';
import { voiceModeService } from '../../utils/voiceMode.service';
import { clearAutoDownloadedFiles } from '../../components/Chat/MessageContentRenderer';
import logger from '../../utils/logger';
import type { PromptAgentType } from './types';

interface UsePromptLibraryConfirmationParams {
  currentPage: 'main' | 'chat';
  hasChatMessages: boolean;
  onClosePromptLibrary: () => void;
  onNavigateToMainPage: () => void;
}

export function usePromptLibraryConfirmation({
  currentPage,
  hasChatMessages,
  onClosePromptLibrary,
  onNavigateToMainPage,
}: UsePromptLibraryConfirmationParams) {
  const dispatch = useDispatch();
  const isVoiceModeActive = useSelector((state: RootState) => state.voiceMode.isActive);
  const showPromptConfirmation = useSelector((state: RootState) => state.chatMode.showPromptConfirmation);
  const pendingPromptSelection = useSelector((state: RootState) => state.chatMode.pendingPromptSelection);

  const handlePromptLibrarySelect = useCallback((prompt: string, tool?: string, agentType?: PromptAgentType) => {
    onClosePromptLibrary();

    if (currentPage === 'chat' && hasChatMessages) {
      dispatch(setPendingPromptSelection({ prompt, tool, agentType }));
      dispatch(setShowPromptConfirmation(true));
      return;
    }

    dispatch(setPromptFromLibrary({ text: prompt, tool }));
    if (agentType && agentType !== 'Ask Chat') {
      dispatch(setRagChatType(agentType as RagChatType));
    } else {
      dispatch(setChatMode('Ask Chat'));
    }
  }, [currentPage, hasChatMessages, dispatch, onClosePromptLibrary]);

  const handleConfirmClearChat = useCallback(() => {
    if (!pendingPromptSelection) return;

    if (isVoiceModeActive) {
      logger.log('[PromptLibrary] Prompt confirmation - stopping voice mode');
      dispatch(cancelTranscribingMessages());
      voiceModeService.disconnect();
      dispatch(setVoiceModeActive(false));
      dispatch(setVoiceModeListening(false));
      dispatch(setVoiceModePaused(false));
      dispatch(setVoiceModeRestarting(false));
      dispatch(setShowInactivityWarning(false));
      dispatch(setInactivityCountdown(0));
      dispatch(setDisconnectedByInactivity(false));
    }

    dispatch(clearaskChatHistory());
    dispatch(clearRagHistory());
    dispatch(clearUploadedFiles());
    dispatch(clearAnalysisResults());
    dispatch(clearTranslationData());
    dispatch(setSelectedTool(null));
    clearAutoDownloadedFiles();
    dispatch(resetSessionId());

    const { prompt, tool, agentType } = pendingPromptSelection;
    if (agentType && agentType !== 'Ask Chat') {
      dispatch(setRagChatType(agentType as RagChatType));
    } else {
      dispatch(setChatMode('Ask Chat'));
    }
    dispatch(setPromptFromLibrary({ text: prompt, tool }));

    onNavigateToMainPage();

    dispatch(clearPendingPromptSelection());
    dispatch(setShowPromptConfirmation(false));
  }, [pendingPromptSelection, isVoiceModeActive, dispatch, onNavigateToMainPage]);

  const handleCancelPromptSelection = useCallback(() => {
    dispatch(clearPendingPromptSelection());
    dispatch(setShowPromptConfirmation(false));
  }, [dispatch]);

  return {
    handlePromptLibrarySelect,
    handleConfirmClearChat,
    handleCancelPromptSelection,
    showPromptConfirmation,
  };
}
